<template>
  <div class="full-width col column">
    <BasicInfoForm
      v-if="isTeamMembers"
      ref="basicInfoFormRef"
      v-model="formData.basicInfo"
      v-model:is-duplicate-value="isDuplicateValue"
      :team-members="isTeamMembers"
    />
    <PStepper
      v-else
      ref="stepper"
      v-model="step"
      class="column no-wrap full-width full-height overflow-auto"
      header-class="full-width no-wrap overflow-auto"
    >
      <template #message>
        <q-separator color="neutral-40" />
      </template>

      <q-step prefix="1" title="BASIC INFO" :done="step > 1" :name="1">
        <BasicInfoForm
          ref="basicInfoFormRef"
          v-model="formData.basicInfo"
          v-model:is-duplicate-value="isDuplicateValue"
          :team-members="isTeamMembers"
        />
      </q-step>

      <q-step
        icon="none"
        prefix="2"
        title="BUSINESS & KYC"
        :done="step > 2"
        :name="2"
      >
        <BusinessKycForm
          ref="businessDetailFormRef"
          v-model="formData.businessDetails"
        />
      </q-step>

      <q-step
        icon="none"
        prefix="3"
        title="FINANCIALS"
        :done="step > 3"
        :name="3"
      >
        <BankDetails
          v-if="formData.basicInfo?.role !== 'buyer'"
          ref="bankDetailFormRef"
          v-model="formData.bankInfo"
        />
        <TaxSettings
          ref="taxSettingsFormRef"
          v-model="formData.taxSettingsInfo"
        />
        <PaymentTerms v-model="formData.paymentsTermsInfo" />
      </q-step>

      <q-step
        icon="none"
        prefix="4"
        title="DOCUMENTS"
        :done="step > 4"
        :name="4"
      >
        <DocumentsCreateForm ref="createDocumentsFormRef" />
      </q-step>

      <q-step icon="none" prefix="5" title="OTHERS" :done="step > 5" :name="5">
        <OthersForm v-model="formData.othersInfo" />
      </q-step>
    </PStepper>
  </div>

  <div class="bg-neutral-10 full-width">
    <q-separator color="neutral-40" />

    <footer class="q-px-2x-md q-py-lg flex justify-between">
      <PBtn
        v-if="step === 4 || step === 5"
        label="Skip"
        :fluid="true"
        flat
        @click="skipAction"
      />

      <div class="flex text-right q-ml-auto">
        <PBtn
          v-if="prevStepCondition"
          label="Back"
          :fluid="false"
          outline
          class="q-ml-xl"
          :disable="step === 1"
          @click="goToPreviousStep"
        />
        <PBtn
          :label="submitBtnLabel"
          :fluid="false"
          :loading="isLoading"
          class="q-ml-xl"
          :disable="step > 5 || isDuplicateValue || isMsmeLoading"
          @click="goToNextStep"
        />
      </div>
    </footer>
  </div>
</template>
<script setup>
import BusinessKycForm from "./BusinessKycForm.vue";
import ConsentInfoDialog from "./ConsentInfoDialog.vue";
import userFormModel from "src/modules/user/models/userForm";
import { useQuasar } from "quasar";
import { omitKeys } from "src/assets/scripts/utilities/helpers";
import { ORG_OMIT_KEYS } from "../constants/userConstants";

const $q = useQuasar();
const { formState, user } = storeToRefs(useUserStore());
const { formState: organisationFormState } = storeToRefs(
  useOrganisationStore(),
);
const { postOrganisation } = useOrganisationStore();

const { removeQueryParams } = useRouterUtilities();

const {
  toggleUserForm,
  postUser,
  putUser,
  putUserBusinessDetails,
  putApplicationStageDetails,
} = useUserStore();

const { putBank } = useBankStore();
const { setOrganisation } = useOrganisationStore();
const { resetBankState } = useBankStore();

const organisationFormRef = ref(null);
const basicInfoFormRef = ref(null);
const businessDetailFormRef = ref(null);
const bankDetailFormRef = ref(null);
const taxSettingsFormRef = ref(null);
const createDocumentsFormRef = ref(null);

const stepper = ref(null);
const step = ref(1);
const financeStepCompleted = ref(false);

const isTeamMembers = inject("isTeamMembers");
const hashRole = inject("hashRole");
const isDuplicateValue = ref(false);
const isLoading = ref(false);
const { queryParams } = useRouterUtilities();

const { formData, closeForm } = useForm({
  formModel: userFormModel(),
  entity: user,
  isForm: formState,
  toggleForm: toggleUserForm,
});

const existingOrg = ref(false);

const setExistingOrg = (value) => {
  existingOrg.value = value;
};

provide("existing-org", { existingOrg, setExistingOrg });

const prevStepCondition = computed(() => {
  return !unref(isTeamMembers);
});

const submitBtnLabel = computed(() => {
  if (unref(step) === 5 || unref(isTeamMembers)) {
    return "Submit";
  } else {
    return "Save & Next";
  }
});

const skipAction = () => {
  return step.value === 5 ? addClose() : stepIncrement();
};

const stepIncrement = () => {
  step.value++;
};

const handleUserBusinessDetailsUpdate = async () => {
  try {
    isLoading.value = true;

    const isBusinessCompleted =
      unref(user).application_stage?.business?.is_completed;

    const { _id, internal_number, is_gst_registered } =
      unref(formData).businessDetails || {};

    const payload = omitKeys(unref(formData).businessDetails, ORG_OMIT_KEYS);

    const orgData = await addOrganisation(unref(formData).businessDetails);

    const orgPayload = {
      role: hashRole.value,
      org_gst: payload.gstin,
      organisations: {
        _id: orgData?._id || _id,
        internal_number: orgData?.internal_number || internal_number,
        gstin: payload?.gstin,
      },
    };

    await putUserBusinessDetails({
      ...orgPayload,
      ...payload,
    });

    const isAddMode = unref(queryParams)?.add;

    if (!isBusinessCompleted && !isAddMode && is_gst_registered) {
      setOrganisation(
        orgData?._id ? orgData : unref(formData).businessDetails || {},
      );
    }

    step.value = 3;
  } catch {
    // Handle error if needed
  } finally {
    isLoading.value = false;
  }
};

const updateUserBusinessDetails = async () => {
  try {
    const isValidated = await unref(
      businessDetailFormRef,
    )?.pForm?.component?.validate();

    if (!isValidated) return;

    const validatePanGst = unref(businessDetailFormRef)?.gstValidationError;

    if (validatePanGst) return;

    const consentStatus =
      unref(formData)?.consent?.terms_and_conditions_privacy?.status_cd;

    if (["pending", "awaiting_response"].includes(consentStatus)) {
      $q.dialog({
        component: ConsentInfoDialog,
        componentProps: {
          headerText: "Consent confirmation",
          labelText: `${unref(formData)?.basicInfo?.name} has not yet accepted the T&C, Privacy Policy and WhatsApp Communication.`,
          hintText: `Do you still want to proceed?`,
          labelClass: "text-neutral text-weight-medium text-md",
          hintClass: "text-neutral-80 text-md",
        },
      })
        .onOk(async () => {
          await handleUserBusinessDetailsUpdate();
        })
        .onCancel(() => {});

      return;
    }

    await handleUserBusinessDetailsUpdate();
  } catch {
    isLoading.value = false;
  }
};

const updateFinancialDetails = async () => {
  try {
    if (!unref(formData)?.basicInfo?.role) return;

    if (unref(formData).basicInfo.role !== "buyer") {
      const isValidatedBankDetail =
        await unref(bankDetailFormRef)?.pForm?.component?.validate();

      if (!isValidatedBankDetail || !unref(bankDetailFormRef).validateCheque())
        return;
    }

    const isValidatedTaxSettings =
      await unref(taxSettingsFormRef)?.pForm?.component?.validate();

    if (!isValidatedTaxSettings) return;

    isLoading.value = true;

    const bankData = unref(formData).bankInfo;
    const taxAndPaymentTerms = {
      ...unref(formData).taxSettingsInfo,
      ...unref(formData).paymentsTermsInfo,
    };

    const bankPromise =
      unref(formData).basicInfo.role !== "buyer" && bankData.file
        ? putBank({
            account_number: bankData.account_number,
            ifsc_code: bankData.ifsc_code,
            bank_name: bankData.bank_name,
            branch_address: bankData.branch_address,
            code: bankData.code,
            bank_beneficiary_name: bankData.bank_beneficiary_name,
            status_cd: bankData.status_cd,
            reason: bankData.reason,
            is_primary: true,
            file: bankData.file,
            file_category: bankData.file_category,
            file_type: bankData.file_type,
            shop_name: bankData.shop_name,
            document_id: bankData.document_id,
            file_id: bankData.file_id,
            user_bank_id: bankData?._id,
          })
        : Promise.resolve();

    const userPromise = putUser({
      credit_cycle: taxAndPaymentTerms.credit_cycle,
      credit_eligiblity: taxAndPaymentTerms.credit_eligiblity,
      credit_limit: taxAndPaymentTerms.credit_limit,
      tax_settings: taxAndPaymentTerms.tax_settings,
      min_net_sales_margin: taxAndPaymentTerms.min_net_sales_margin,
      role: unref(formData).basicInfo.role,
      ...(unref(formData).basicInfo.role === "seller" && {
        is_advance_required: taxAndPaymentTerms.is_advance_required === true,
      }),
    });

    await Promise.all([bankPromise, userPromise]);

    step.value = 4;

    financeStepCompleted.value = true;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const updateDocumentsDetails = async () => {
  try {
    if (!unref(createDocumentsFormRef).validateDocs()) return;

    await putApplicationStageDetails({
      params: { application_status: unref(formData)?.application_status },
    });

    step.value = 5;
  } catch (error) {
    console.error(error);
  }
};

const goToPreviousStep = async () => {
  if (step.value === 1) {
    organisationFormState.value.edit = true;
  }

  step.value--;
};

const goToNextStep = async () => {
  switch (step.value) {
    case 1:
      await addUser();

      break;
    case 2:
      await updateUserBusinessDetails();

      break;
    case 3:
      await updateFinancialDetails();

      break;
    case 4:
      await updateDocumentsDetails();

      break;
    case 5:
      await submitForm();

      break;

    default:
      stepIncrement();

      break;
  }
};

const onGoingOnboarding = ref(false);

const addOrganisation = async (businessDetails) => {
  let orgData = {};

  if (
    !businessDetails?._id &&
    !unref(queryParams)?.add &&
    !unref(user).application_stage?.business?.is_completed
  ) {
    const payload = {
      pan: businessDetails.pan,
      name: businessDetails.shop_name,
      is_gst_registered: businessDetails.is_gst_registered,
    };

    if (businessDetails.is_gst_registered) {
      payload.gstinList = unref(formData).businessDetails.gstinList;
    }

    const response = await postOrganisation(payload, {
      params: { org: true },
    });

    orgData = response?.data?.data;
  }

  return orgData || {};
};

//add user
const addUser = async () => {
  try {
    resetBankState();

    const isValidated =
      await unref(basicInfoFormRef)?.pForm?.component?.validate();

    if (!isValidated) return;

    isLoading.value = true;

    const { phone } = unref(formData).basicInfo || {};
    const userPhone = unref(user).basicInfo?.phone;

    if (phone) {
      if (phone === userPhone) {
        await putUser(unref(formData).basicInfo);
      } else {
        await postUser(unref(formData).basicInfo);
      }
    } else {
      await postUser(unref(formData).basicInfo);
    }

    onGoingOnboarding.value = true;

    if (unref(isTeamMembers)) {
      addClose();
    } else {
      step.value++;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const isMsmeLoading = computed(() => {
  return !!businessDetailFormRef.value?.msmeLoading;
});

const submitForm = async () => {
  try {
    isLoading.value = true;

    const role = unref(formData).basicInfo?.role;

    const payload = {
      ...unref(formData).othersInfo,
      role: unref(formData).basicInfo?.role,
    };

    if (role === "buyer") {
      payload.po_applicable = Boolean(
        unref(user)?.othersInfo?.poSetting?.po_applicable,
      );

      payload.po_file_applicable = Boolean(
        unref(user)?.othersInfo?.poSetting?.po_file_applicable,
      );
    }

    await putUser(payload);

    addClose();
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;

    removeQueryParams();
  }
};

const addClose = async () => {
  step.value = 1;

  onGoingOnboarding.value = false;

  financeStepCompleted.value = false;

  organisationFormState.value.edit = false;

  setOrganisation("");

  await unref(organisationFormRef)?.onClose();

  closeForm(true);
};

defineExpose({ addClose });

provide("onGoingOnboarding", readonly(onGoingOnboarding));

provide("financeStepCompleted", financeStepCompleted);
</script>
